import { useId, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { useNavigate } from "react-router";
import { statusHides, type BrainItem } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useBrainItems } from "./brain-api";
import { BrainItemRow } from "./brain-item-row";
import { StatusShelfRow } from "./status-shelf-row";
import type { ShelfFilter } from "./source-shelves";

/**
 * One shelf's contents, opened over the Sources tab.
 *
 * This replaces the search-and-list that used to sit permanently below the
 * shelves. That list answered "everything, filtered" — but the shelves are
 * already the question, so the page carried a filter row whose job the cards
 * above it had done, and a reader had to scroll past the shelves to see what
 * clicking one had achieved.
 *
 * Its own query state rather than the page's: a shelf is opened, browsed and
 * closed, and its search term should not survive into the next shelf or leak
 * into the page's URL.
 *
 * The shelf's filter goes to the list as it is — a source type, a category,
 * or the files waiting for a decision — so what opens is exactly what the
 * shelf counted.
 *
 * A category's shelf has a second list under its own, folded: "Also relevant
 * here", the files that live in another category and list this one
 * (`document-ai/0032`). It is its own query, with its own count and pages, so
 * the shelf's count stays the list it opens. Each of its rows says where the
 * file lives.
 *
 * On a shelf that asks about a hidden status, each file has its answers in its
 * row (2026-09-26, HID-11): Keep and Remove, and Hide for a file the check
 * asked about. What was done is said at the top, since the file leaves the
 * list once it is re-read.
 */

const PAGE_SIZE = 50;

export function ShelfDialog({
  projectId,
  filter,
  title,
  onClose,
}: {
  projectId: string;
  filter: ShelfFilter;
  title: string;
  onClose: () => void;
}) {
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(0);
  const [relevantPage, setRelevantPage] = useState(0);
  const [done, setDone] = useState<string[]>([]);
  const navigate = useNavigate();
  const answers = filter.status !== undefined && statusHides(filter.status);
  const q = term.trim() || undefined;

  const { data, isPending } = useBrainItems(projectId, {
    q,
    ...filter,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const open = (item: BrainItem): void => {
    // Navigating away from a modal has to close it, or the reader lands on the
    // entry with the dialog still stacked over it.
    onClose();
    void navigate(
      item.kind === "document"
        ? // The matched page rides along, so a search hit opens *where* it hit
          // rather than at page one of a seventy-five page document.
          `/projects/${projectId}/brain/files/${item.id}${
            item.matchedPage ? `?page=${item.matchedPage}` : ""
          }`
        : `/projects/${projectId}/brain/${item.id}`,
    );
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="3xl" className="h-[86vh]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="mb-3 flex shrink-0 items-center gap-3">
            <Input
              value={term}
              // A category's name is the tenant's own, in its language and
              // with its capitals, so it is not lower-cased into the sentence.
              // "Needs a decision" is not a noun to search either.
              placeholder={
                filter.sourceType ? `Search ${title.toLowerCase()}` : "Search these files"
              }
              aria-label={`Search ${title}`}
              className="max-w-[360px]"
              onChange={(event) => {
                setTerm(event.target.value);
                setPage(0);
                setRelevantPage(0);
              }}
            />
            {data && (
              <span className="text-[0.82rem] text-muted">
                {data.total} {data.total === 1 ? "item" : "items"}
              </span>
            )}
          </div>

          {done.length > 0 && (
            <ul className="mb-3 flex shrink-0 flex-col gap-0.5">
              {done.map((sentence, index) => (
                <li key={`${index}-${sentence}`} role="status" className="text-[0.85rem] font-semibold text-ok">
                  {sentence}
                </li>
              ))}
            </ul>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isPending ? (
              <div className="flex items-center gap-2 py-10 text-muted" role="status">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-[0.9rem]">Loading…</span>
              </div>
            ) : !data || data.items.length === 0 ? (
              <p className="py-10 text-center text-[0.9rem] text-muted">
                {q ? `Nothing matches “${q}”.` : "Nothing on this shelf yet."}
              </p>
            ) : (
              <ul className="overflow-hidden rounded-card-lg border-[1.5px] border-border">
                {data.items.map((item) =>
                  answers && item.kind === "document" ? (
                    <StatusShelfRow
                      key={`${item.kind}-${item.id}`}
                      item={item}
                      projectId={projectId}
                      onOpen={() => open(item)}
                      onDone={(sentence) => setDone((before) => [...before, sentence])}
                    />
                  ) : (
                    <BrainItemRow
                      key={`${item.kind}-${item.id}`}
                      item={item}
                      projectId={projectId}
                      onOpen={() => open(item)}
                      // Every row here lives in it, so none says so.
                      shelfCategory={filter.category}
                    />
                  ),
                )}
              </ul>
            )}

            {filter.category !== undefined && (
              <AlsoRelevant
                projectId={projectId}
                category={filter.category}
                q={q}
                page={relevantPage}
                onPage={setRelevantPage}
                // A shelf that opened only for these shows them at once.
                nothingOfItsOwn={data !== undefined && !isPending && data.total === 0}
                onOpen={open}
              />
            )}
          </div>

          {data && <Pager page={page} total={data.total} onPage={setPage} className="mt-3 shrink-0" />}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A category shelf's "Also relevant here" (`document-ai/0032`): the files
 * that live in another category and list this one. A person browsing Ramming
 * would otherwise not see an NCR about pile refusal that lives in Quality.
 *
 * Folded, because the shelf is its own files first. Not drawn at all when
 * there are none. Its own query with `relevant=<key>`, so its count and pages
 * are its own and the shelf's count stays the list above.
 */
function AlsoRelevant({
  projectId,
  category,
  q,
  page,
  onPage,
  nothingOfItsOwn,
  onOpen,
}: {
  projectId: string;
  category: string;
  q: string | undefined;
  page: number;
  onPage: (page: number) => void;
  nothingOfItsOwn: boolean;
  onOpen: (item: BrainItem) => void;
}) {
  const ids = useId();
  // Null until a person folds or unfolds it; until then it follows the shelf.
  const [unfolded, setUnfolded] = useState<boolean | null>(null);
  const { data } = useBrainItems(projectId, {
    q,
    relevant: category,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  if (!data || data.total === 0) return null;
  const shown = unfolded ?? nothingOfItsOwn;

  return (
    <section aria-labelledby={`${ids}-title`} className="mt-4">
      <button
        type="button"
        aria-expanded={shown}
        aria-controls={`${ids}-list`}
        onClick={() => setUnfolded(!shown)}
        className="flex w-full cursor-pointer items-center gap-2 rounded-input px-1 py-1.5 text-left hover:bg-subtle"
      >
        <ChevronDown
          size={16}
          aria-hidden
          className={cn("shrink-0 text-muted transition-transform", !shown && "-rotate-90")}
        />
        <span id={`${ids}-title`} className="font-display text-[0.9rem] font-semibold text-ink">
          Also relevant here
        </span>
        <span className="text-[0.82rem] text-muted">
          {data.total} {data.total === 1 ? "item" : "items"}
        </span>
      </button>
      {shown && (
        <div id={`${ids}-list`} className="mt-2">
          <ul className="overflow-hidden rounded-card-lg border-[1.5px] border-border">
            {data.items.map((item) => (
              <BrainItemRow
                key={`${item.kind}-${item.id}`}
                item={item}
                projectId={projectId}
                onOpen={() => onOpen(item)}
                // Each row says where the file lives: "In Quality control".
                shelfCategory={category}
              />
            ))}
          </ul>
          <Pager page={page} total={data.total} onPage={onPage} className="mt-3" />
        </div>
      )}
    </section>
  );
}

/** Previous, "51–100 of 120", Next. Nothing while one page holds them all. */
function Pager({
  page,
  total,
  onPage,
  className,
}: {
  page: number;
  total: number;
  onPage: (page: number) => void;
  className?: string;
}) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => onPage(Math.max(0, page - 1))}>
        Previous
      </Button>
      <span className="text-[0.82rem] text-muted">
        {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
      </span>
      <Button
        variant="secondary"
        size="sm"
        disabled={(page + 1) * PAGE_SIZE >= total}
        onClick={() => onPage(page + 1)}
      >
        Next
      </Button>
    </div>
  );
}
